import React, { useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRemoveWidget, useUpdateWidget } from '../../hooks/useDashboard';
import { useStreamComplete } from '../../hooks/useWidgetData';
import WidgetContent from './WidgetContent';
import { GripVertical, X, ChevronUp, ChevronDown, Maximize2, RefreshCw, AlertCircle, Info, Pencil } from 'lucide-react';
import { Modal } from 'antd';
import { toast } from '@rickylandino/react-messages';
import Globals from '../../config/globals';

/**
 * DashboardWidget component
 * Individual draggable widget with controls
 */
export default function DashboardWidget({ widget, editMode }) {
const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver
} = useSortable({
    id: widget?.widgetInstanceKey ?? '__dragoverlay__',
    disabled: !widget?.widgetInstanceKey
});

    const removeWidget = useRemoveWidget();
    const updateWidget = useUpdateWidget();
    const streamComplete = useStreamComplete();
    const isSystemWidget = widget?.widgetDefinition?.widgetCategory === 'system';

    // Parse filterConfig the same way child widgets do � handles null, '{}', '"null"', etc.
    const parsedFilterConfig = (() => {
        if (!widget?.filterConfig) return null;
        if (typeof widget.filterConfig === 'string') {
            try { return JSON.parse(widget.filterConfig); } catch { return null; }
        }
        return widget.filterConfig;
    })();
    // A user widget is "configured" when it has at least one saved facility
    const isUserWidgetConfigured = !isSystemWidget && (parsedFilterConfig?.facilityIds?.length ?? 0) > 0;

    // Configured user widgets start collapsed (count view); new/unconfigured widgets start uncollapsed (filter form)
    const initialCollapsed = isSystemWidget || isUserWidgetConfigured;
    const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isWidgetLoading, setIsWidgetLoading] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [showDescription, setShowDescription] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameTitle, setRenameTitle] = useState('');
    const [renameDescription, setRenameDescription] = useState('');
    const [sharedState, setSharedState] = useState({
        isCollapsed: initialCollapsed,
        isExpanded: false,
        setIsLoading: setIsWidgetLoading,
        refetch: null,
        queryEnabled: false,
        hasData: isSystemWidget || isUserWidgetConfigured
    });

    // Enable individual count queries only after the stream finishes (or times out).
    // The stream is the primary data source; individual queries are the fallback.
    // This prevents double-hitting SQL for the same widget. Mirrors streamComplete both
    // ways (not just false->true) so a stream re-opened after a facility change re-gates
    // individual queries too, instead of leaving them permanently enabled from the first run.
    useEffect(() => {
        setSharedState(prev => ({ ...prev, queryEnabled: streamComplete }));
    }, [streamComplete]);

    if (!widget) return null;


    // Detect if this is a system widget (cannot be deleted)
    const effectiveDescription = isSystemWidget
        ? widget.widgetDefinition?.widgetDescription
        : widget.widgetDescription;
    const effectiveCollapsed = isCollapsed;
    const canExpand = isSystemWidget || sharedState.hasData;


    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto'
    };

    const handleRemoveClick = () => {
        // Only widgets with a valid ID can be removed
        if (!widget.userDashboardWidgetId) {
            console.warn('Cannot remove widget without valid ID');
            toast.error('Unable to remove this widget');
            return;
        }
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = () => {
        if (widget.userDashboardWidgetId) {
            removeWidget.mutate(widget.userDashboardWidgetId, {
                onSuccess: () => {
                    toast.success('Widget removed from dashboard');
                    setDeleteModalVisible(false);
                },
                onError: (error) => {
                    console.error('Failed to remove widget:', error);
                    toast.error('Failed to remove widget');
                    setDeleteModalVisible(false);
                },
            });
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
    };

    const handleToggleCollapse = () => {
        const newCollapsed = !isCollapsed;
        setIsCollapsed(newCollapsed);
        setSharedState(prev => ({ ...prev, isCollapsed: newCollapsed }));
        // Optionally save to backend
        // dashboardService.toggleCollapsed(widget.userDashboardWidgetId, newCollapsed);
    };

    const handleExpand = () => {
        const newExpanded = !isExpanded;
        setIsExpanded(newExpanded);
        setSharedState(prev => ({ ...prev, isExpanded: newExpanded }));
        
        // If closing expanded view, reset widget loading state
        if (!newExpanded) {
            setIsWidgetLoading(false);
        }
    };

    const handleRefresh = () => {
        if (sharedState.refetch && typeof sharedState.refetch === 'function') {
            sharedState.refetch();
        }
    };

    const handleRenameConfirm = () => {
        if (!renameTitle?.trim()) {
            toast.error('Please enter a name for this widget');
            return;
        }
        updateWidget.mutate({
            userDashboardWidgetId: widget.userDashboardWidgetId,
            request: {
                displayOrder: widget.displayOrder ?? 0,
                rowPosition: widget.rowPosition ?? 0,
                columnPosition: widget.columnPosition ?? 0,
                columnSpan: widget.columnSpan ?? 1,
                isCollapsed,
                widgetTitle: renameTitle.trim(),
                widgetDescription: renameDescription?.trim() || null,
                filterConfig: widget.filterConfig ?? null
            }
        }, {
            onSuccess: () => {
                toast.success('Widget updated');
                setShowRenameModal(false);
            },
            onError: () => {
                toast.error('Failed to update widget');
            }
        });
    };

    return (
        <>
            {/* Normal widget card */}
            <div className={isExpanded ? 'hidden' : 'relative'}>
                {/* Drop indicator - shows where the dragged widget will be inserted */}
                {isOver && editMode && (
                    <div className="absolute -top-2 left-0 right-0 h-1 bg-pink-500 dark:bg-pink-400 rounded-full shadow-lg shadow-pink-500/50 dark:shadow-pink-400/50 z-10 animate-pulse" />
                )}
                
                <div
                    ref={setNodeRef}
                    style={style}
                    {...attributes}
                    className="rounded-xl cursor-default"
                >
                    <div 
                        className="rounded-xl h-full border-2 shadow-md transition-all duration-200"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#2b2b2b' : '#fdf2f8',
                            borderColor: Globals.theme === 'dark' ? '#ec4899' : '#f9a8d4',
                            boxShadow: Globals.theme === 'dark' 
                                ? '0 0 0 1px rgba(236, 72, 153, 0.3)' 
                                : '0 4px 6px -1px rgba(236, 72, 153, 0.1), 0 2px 4px -2px rgba(236, 72, 153, 0.05)'
                        }}
                    >
                        {/* Card Header */}
                        <div 
                            className="px-4 py-3 border-b bg-transparent"
                            style={{
                                borderColor: Globals.theme === 'dark' ? '#3f3f46' : '#fbcfe8'
                            }}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {editMode && (
                                        <button
                                            {...listeners}
                                            className="cursor-grab cursor-move active:cursor-grabbing"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#a1a1aa' : '#ec4899'
                                            }}
                                        >
                                            <GripVertical className="w-5 h-5" />
                                        </button>
                                    )}
                                    <h3 
                                        className="text-lg font-semibold truncate"
                                        style={{
                                            color: Globals.theme === 'dark' ? 'white' : '#9f1239'
                                        }}
                                    >
                                        {widget.widgetTitle || widget.widgetDefinition?.widgetName || 'Untitled Widget'}
                                    </h3>
                                </div>
                                <div className="flex items-center gap-1">
                                    {effectiveDescription && (
                                        <button
                                            onClick={() => setShowDescription(prev => !prev)}
                                            className="p-1 rounded transition-colors cursor-pointer"
                                            style={{
                                                color: showDescription
                                                    ? (Globals.theme === 'dark' ? '#ec4899' : '#db2777')
                                                    : (Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899')
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title={showDescription ? 'Hide description' : 'Show description'}
                                        >
                                            <Info className="w-4 h-4" />
                                        </button>
                                    )}
                                    {isSystemWidget && sharedState.refetch && (
                                        <button
                                            onClick={handleRefresh}
                                            className="p-1 rounded transition-colors cursor-pointer"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Refresh widget data"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={canExpand ? handleExpand : undefined}
                                        disabled={!canExpand}
                                        className="p-1 rounded transition-colors"
                                        style={{
                                            color: canExpand ? (Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899') : (Globals.theme === 'dark' ? '#52525b' : '#d1d5db'),
                                            cursor: canExpand ? 'pointer' : 'not-allowed'
                                        }}
                                        onMouseEnter={(e) => { if (canExpand) e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'; }}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        title={canExpand ? 'Expand widget' : 'Generate a report to enable expand'}
                                    >
                                        <Maximize2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleToggleCollapse}
                                        className="p-1 rounded transition-colors cursor-pointer"
                                        style={{
                                            color: Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        title={isCollapsed ? 'Expand' : 'Collapse'}
                                    >
                                        {isCollapsed ? (
                                            <ChevronDown className="w-4 h-4" />
                                        ) : (
                                            <ChevronUp className="w-4 h-4" />
                                        )}
                                    </button>
                                    {/* Rename button for user widgets */}
                                    {!isSystemWidget && widget.userDashboardWidgetId && (
                                        <button
                                            onClick={() => {
                                                setRenameTitle(widget.widgetTitle || widget.widgetDefinition?.widgetName || '');
                                                setRenameDescription(widget.widgetDescription || '');
                                                setShowRenameModal(true);
                                            }}
                                            className="p-1 rounded transition-colors cursor-pointer"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#d4d4d8' : '#ec4899'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#fce7f3'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Rename widget"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                    )}
                                    {/* Remove button only for user widgets (not system widgets) */}
                                    {!isSystemWidget && widget.userDashboardWidgetId && (
                                        <button
                                            onClick={handleRemoveClick}
                                            className="p-1 rounded transition-colors cursor-pointer"
                                            style={{
                                                color: Globals.theme === 'dark' ? '#f87171' : '#dc2626'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#7f1d1d' : '#fee2e2'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Remove widget"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {showDescription && effectiveDescription && (
                            <div
                                className="px-4 py-2 border-b text-sm"
                                style={{
                                    borderColor: Globals.theme === 'dark' ? '#3f3f46' : '#fbcfe8',
                                    color: Globals.theme === 'dark' ? '#a1a1aa' : '#6b7280',
                                    backgroundColor: Globals.theme === 'dark' ? '#232323' : '#fef9fb'
                                }}
                            >
                                {effectiveDescription}
                            </div>
                        )}

                        <div className="p-2">
                        <WidgetContent 
                            widget={widget} 
                            sharedState={{ ...sharedState, isCollapsed: effectiveCollapsed }}
                            setSharedState={setSharedState}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded fullscreen overlay */}
            {isExpanded && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-200 cursor-pointer"
                    onClick={handleExpand}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Escape' && handleExpand()}
                    aria-label="Close expanded view"
                >
                    {/* Glassmorphism backdrop */}
                    <div 
                        className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-pink-600/5 backdrop-blur-xl"
                        style={{ backgroundColor: 'rgba(219, 39, 119, 0.15)' }}
                    />

                    <div
                        className="relative z-10 w-[92vw] h-[90vh] bg-white/95 dark:bg-[#2b2b2b]/95 backdrop-blur-sm rounded-2xl shadow-2xl border-2 border-pink-200/50 dark:border-pink-500/80 flex flex-col animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        {/* Loading Overlay */}
                        {isWidgetLoading && (
                            <div 
                                className="absolute inset-0 flex items-center justify-center rounded-2xl z-20"
                                style={{
                                    backgroundColor: Globals.theme === 'dark' ? 'rgba(43, 43, 43, 0.9)' : 'rgba(255, 255, 255, 0.9)'
                                }}
                            >
                                <div className="flex flex-col items-center gap-3">
                                    <div 
                                        className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
                                        style={{ 
                                            borderColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                                            borderTopColor: 'transparent'
                                        }}
                                    />
                                    <span 
                                        className="text-sm font-medium"
                                        style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                                    >
                                        Loading widget data...
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between px-8 py-6 border-b border-zinc-200 dark:border-zinc-700 bg-transparent">
                            <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">
                                {widget.widgetTitle || widget.widgetDefinition?.widgetName || 'Untitled Widget'}
                            </h2>
                            <div className="flex items-center gap-2">
                                {sharedState.refetch && (
                                <button
                                        onClick={handleRefresh}
                                        className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors cursor-pointer"
                                        title="Refresh widget data"
                                    >
                                        <RefreshCw className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                                    </button>
                                )}
                                <button
                                    onClick={handleExpand}
                                    className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors cursor-pointer"
                                >
                                    <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto px-8 py-6">
                            <WidgetContent 
                                widget={widget} 
                                sharedState={sharedState}
                                setSharedState={setSharedState}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Widget Confirmation Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertCircle 
                            style={{ 
                                color: '#dc2626',
                                width: '20px',
                                height: '20px'
                            }} 
                        />
                        <span>Remove Widget from Dashboard</span>
                    </div>
                }
                visible={deleteModalVisible}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                okText="Remove Widget"
                cancelText="Cancel"
                okButtonProps={{
                    danger: true,
                    style: {
                        backgroundColor: '#dc2626',
                        borderColor: '#dc2626'
                    }
                }}
            >
                <div>
                    <p style={{ marginBottom: '12px' }}>
                        This widget will be permanently removed from your dashboard. You can add it back later if you choose.
                    </p>
                    <p style={{ fontWeight: 500 }}>
                        Are you sure you want to continue?
                    </p>
                </div>
            </Modal>

            <Modal
                title="Rename Widget"
                visible={showRenameModal}
                onOk={handleRenameConfirm}
                onCancel={() => setShowRenameModal(false)}
                okText="Save"
                okButtonProps={{ disabled: !renameTitle?.trim() || updateWidget.isLoading }}
                destroyOnClose
            >
                <div style={{ paddingTop: '8px' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <label className="form-label">Widget Name <span className="color-pink">*</span></label>
                        <input
                            className="form-control-custom"
                            value={renameTitle}
                            onChange={(e) => setRenameTitle(e.target.value)}
                            placeholder="Enter a name for this widget"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="form-label">Description (optional)</label>
                        <textarea
                            className="form-control-custom"
                            rows={3}
                            value={renameDescription}
                            onChange={(e) => setRenameDescription(e.target.value)}
                            placeholder="Optional description or notes"
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
}
